import { useState } from "react";
import { RequiredIndicator } from "../../components";
import { useMutation, useQuery } from "react-query";
import _ from "lodash";
import { ATTENDANCE_PURPOSE } from "../../helpers/config";
import { Select, Skeleton, Modal } from "@mantine/core";
import {
  Home,
  LogOut,
  UserPlus,
  UserCheck,
  ArrowLeft,
  List,
} from "lucide-react";
import {
  getAllPublicFacilities,
  postPublicAttendance,
  putPublicAttendance,
} from "../../helpers/api";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import NewMemberForm from "../external/new-member";
import { PublicSingleRegistration } from "../member/public/single";
import { ServicesPublicModal } from "./services";
import { RiviaCareTabs } from "./riviacare";

let attendanceStateTemplate = {
  firstName: "",
  lastName: "",
  contact: "",
  email: "",
  purpose: "",
  attendanceType: "",
  timeIn: null,
  timeOut: null,
  id: null,
};

const KioskDashboard = (props) => {
  const [state, setState] = useState(attendanceStateTemplate);
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
    size: "xl",
  });
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const nav = useNavigate();
  // const [ outState, setOutState ] = useState( { contact: '', lastName: '', firstName: '', notes: '' } );

  const { data: facilities = [], isFetching } = useQuery({
    queryFn: getAllPublicFacilities,
    queryKey: ["all-facilities"],
  });

  // Helper to get selected clinic object
  const selectedClinicObj =
    facilities.find((f) => f.id === selectedClinic) || null;

  // Reset state if clinic changes
  const handleClinicChange = (value) => {
    setState(attendanceStateTemplate); // reset any in-progress state
    setSelectedClinic(value);
  };

  // Optionally allow changing clinic after selection
  const handleChangeClinic = () => {
    setState(attendanceStateTemplate);
    setSelectedClinic(null);
  };

  const { mutateAsync: ClockIn, isLoading } = useMutation(
    (data) => postPublicAttendance(data),
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 201) {
          message.success(data.data.message);
          setState(attendanceStateTemplate);
          return;
        }

        throw data;
      },
      onError: (error, variables, context) => {
        const err = error.response.data.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err);
        }
      },
    }
  );

  const { mutateAsync: ClockOut, isLoading: isClockingOut } = useMutation(
    (data) => putPublicAttendance(data),
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 200) {
          message.success(data.data.message);
          setState(attendanceStateTemplate);
          return;
        }

        throw data;
      },
      onError: (error, variables, context) => {
        const err = error.response.data.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err);
        }
      },
    }
  );

  return (
    <div className="container py-4">
      <Modal
        opened={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        size={modal.size}>
        {modal.content}
      </Modal>

      {/* <Modal
        opened={showServicesModal}
        onClose={() => setShowServicesModal(false)}
        title="Our Services"
        size="xl">
        <ServicesPublicModal
          facilityId={selectedClinicObj.id}
          open={showServicesModal}
          onClose={() => setShowServicesModal(false)}
        />
      </Modal> */}

      <div className="text-center mb-4">
        <h3 className="display-6 fw-bold app-color">
          {selectedClinicObj
            ? `Welcome to '${selectedClinicObj.facilityName}'`
            : "Welcome to Rivia"}
        </h3>
        <p className="lead text-muted">
          {selectedClinicObj
            ? "Please select an option below to get started."
            : "Please select your provider to begin."}
        </p>
        {selectedClinicObj && (
          <button
            className="btn btn-link btn-sm mt-2"
            style={{ textDecoration: "underline" }}
            onClick={handleChangeClinic}>
            Change Provider
          </button>
        )}
      </div>
      {
        // Step 1: Require clinic selection
        !selectedClinicObj && (
          <div className="row justify-content-center">
            <div className="col-md-6 col">
              <div className="field mb-4">
                <label className="mb-2 label" htmlFor="clinic-select">
                  Select Provider <RequiredIndicator />
                </label>
                <Select
                  id="clinic-select"
                  placeholder="Choose your provider"
                  size="xl"
                  searchable
                  nothingFound="No providers found"
                  value={selectedClinic}
                  onChange={handleClinicChange}
                  data={facilities.map((f) => ({
                    value: f.id,
                    label: f.facilityName,
                  }))}
                  disabled={isFetching}
                />
              </div>
            </div>
          </div>
        )
      }
      {
        // Step 2: Show menu only after clinic is selected
        selectedClinicObj && !state?.attendanceType && (
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col">
              <button
                onClick={() =>
                  setState({ ...state, attendanceType: "checkin" })
                }
                className="button w-100 py-4 rounded-3 shadow-sm d-flex flex-column align-items-center justify-content-center app-btn is-large  mb-0"
                style={{ minHeight: "120px" }}>
                <Home size={36} className="mb-2" />
                Check In
              </button>
            </div>
            <div className="col-md-6 col">
              <button
                onClick={() =>
                  setState({ ...state, attendanceType: "checkout" })
                }
                className="button w-100 py-4 rounded-3 shadow-sm d-flex flex-column align-items-center justify-content-center app-btn is-large  mb-0"
                style={{ minHeight: "120px" }}>
                <LogOut size={36} className="mb-2" />
                Check Out
              </button>
            </div>
            <div className="col-md-6 col">
              <button
                onClick={() =>
                  setModal({
                    isOpen: true,
                    title: "Register Member",
                    content: (
                      <NewMemberForm
                        onSuccess={() => {
                          message.success("Registration successful!");
                          setModal({ ...modal, isOpen: false });
                        }}
                        showCharm={false}
                        showBottomButtons
                        facilityId={selectedClinicObj.id}
                      />
                    ),
                    size: "lg",
                  })
                }
                className="button w-100 py-4 rounded-3 shadow-sm d-flex flex-column align-items-center justify-content-center app-btn is-large  mb-0"
                style={{ minHeight: "120px" }}>
                <UserPlus size={36} className="mb-2" />
                Register Member
              </button>
            </div>
            <div className="col-md-6 col">
              <button
                onClick={() =>
                  setModal({
                    isOpen: true,
                    title: "Our Services",
                    content: (
                      <ServicesPublicModal facilityId={selectedClinicObj.id} />
                    ),
                    size: "xl",
                  })
                }
                disabled={!selectedClinic}
                className="button w-100 py-4 rounded-3 shadow-sm d-flex flex-column align-items-center justify-content-center app-btn is-large mb-0 btn-info"
                style={{ minHeight: "120px" }}
                title={
                  !selectedClinic
                    ? "Select a clinic to view its services"
                    : undefined
                }>
                <List size={36} className="mb-2" />
                Our Services
              </button>
            </div>
            {/* <div className="col-md-6 col">
              <button
                disabled
                onClick={() =>
                  setModal({
                    isOpen: true,
                    title: "RiviaCare",
                    content: <RiviaCareTabs />,
                    size: "lg",
                  })
                }
                className="button w-100 py-4 rounded-3 shadow-sm d-flex flex-column align-items-center justify-content-center app-btn is-large  mb-0 btn-info"
                style={{ minHeight: "120px" }}>
                <UserCheck size={36} className="mb-2" />
                RiviaCare
              </button>
            </div> */}
          </div>
        )
      }
      {
        // Step 3: Pass selected clinic to check-in form and hide clinic select
        selectedClinicObj && state?.attendanceType === "checkin" && (
          <>
            <button
              onClick={() => setState({ attendanceType: "" })}
              className="button mb-4 app-btn h5">
              <ArrowLeft className="me-3" />
              Go Back
            </button>
            <CheckInForm
              state={{ ...state, id: selectedClinicObj.id }}
              setState={({ field, value }) =>
                setState({ ...state, [field]: value })
              }
              onSubmit={() =>
                ClockIn({
                  ...state,
                  id: selectedClinicObj.id,
                  facilityId: selectedClinicObj.id,
                })
              }
              isSubmitting={isLoading}
              isFetching={false}
              facilities={[selectedClinicObj]}
              hideClinicSelect={true}
              selectedClinicObj={selectedClinicObj}
            />
          </>
        )
      }
      {selectedClinicObj && state?.attendanceType === "checkout" && (
        <>
          <button
            onClick={() => setState({ attendanceType: "" })}
            className="button mb-4 app-btn h5">
            <ArrowLeft className="me-3" /> Go Back
          </button>
          <CheckOutForm
            state={state}
            setState={({ field, value }) =>
              setState({ ...state, [field]: value })
            }
            onSubmit={() =>
              ClockOut({ ...state, facilityId: selectedClinicObj.id })
            }
            isSubmitting={isClockingOut}
            isFetching={false}
          />
        </>
      )}
    </div>
  );
};

export default KioskDashboard;

const CheckInForm = ({
  state,
  setState,
  facilities = [],
  onSubmit,
  isSubmitting,
  isFetching,
  hideClinicSelect,
  selectedClinicObj,
}) => {
  return (
    <div autoComplete="off" className="row">
      {!hideClinicSelect && (
        <div className="col-12 mb-3">
          {isFetching ? (
            <Skeleton>Loading</Skeleton>
          ) : (
            <div className="field">
              <label className="mb-0 label" htmlFor="facility">
                Where are you?
                <RequiredIndicator />
              </label>
              <Select
                id="facility"
                nothingFound="No match"
                size="xl"
                clearable
                searchable
                value={state?.id}
                onChange={(value) => setState({ field: "id", value })}
                placeholder="name of Rivia Clinic"
                data={facilities?.map((d) => {
                  return {
                    value: d.id,
                    label: d.facilityName,
                  };
                })}
              />
            </div>
          )}
        </div>
      )}
      {state?.id && (
        <>
          <div className="col-md-6 col-12 mb-3">
            <div className="field">
              <label className="mb-0 label" htmlFor="firstName">
                First Name
                <RequiredIndicator />
              </label>
              <input
                required
                id="firstName"
                autoComplete="new"
                placeholder="first name"
                value={state?.firstName}
                onChange={(e) =>
                  setState({ field: "firstName", value: e.target.value })
                }
                type="text"
                className="input is-large"
              />
            </div>
          </div>
          <div className="col-md-6 col-12 mb-3">
            <div className="field">
              <label className="mb-0 label" htmlFor="lastName">
                Last Name
                <RequiredIndicator />
              </label>
              <input
                required
                id="lastName"
                placeholder="last name"
                autoComplete="new-pas"
                value={state?.lastName}
                onChange={(e) =>
                  setState({ field: "lastName", value: e.target.value })
                }
                type="text"
                className="input is-large"
              />
            </div>
          </div>
          <div className="col-md-6 col-12 mb-3">
            <div className="field">
              <label className="mb-0 label" htmlFor="contact">
                Phone Number
                <RequiredIndicator />
              </label>
              <input
                required
                id="contact"
                placeholder="active phone number"
                autoComplete="new-pass"
                type="tel"
                value={state?.contact}
                onChange={(e) =>
                  setState({ field: "contact", value: e.target.value })
                }
                className="input is-large"
              />
            </div>
          </div>
          <div className="col-md-6 col-12 mb-3">
            <div className="field">
              <label className="mb-0 label" htmlFor="purpose">
                Purpose
                <RequiredIndicator />
              </label>
              <Select
                id="purpose"
                nothingFound="No match"
                size="xl"
                clearable
                searchable
                value={state?.purpose}
                onChange={(value) => setState({ field: "purpose", value })}
                placeholder="purpose of visit"
                data={ATTENDANCE_PURPOSE.map((d) => {
                  return {
                    value: d.value,
                    label: d.label,
                  };
                })}
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="field">
              <label className="mb-0 label" htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="new-passw"
                value={state?.email}
                onChange={(e) =>
                  setState({ field: "email", value: e.target.value })
                }
                placeholder="active email address (optional)"
                className="input is-large"
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <div className="field">
              <label className="mb-0 label" htmlFor="notes">
                Notes
              </label>
              <textarea
                id="notes"
                value={state?.notes}
                // autoComplete="new"
                onChange={(e) =>
                  setState({ field: "notes", value: e.target.value })
                }
                placeholder="share additional information (optional)"
                className="textarea is-large"
              />
            </div>
          </div>
          <div className="col-12">
            <button
              onClick={onSubmit}
              className={`button h6 is-large app-btn btn-prim mt-3 ${isSubmitting && "is-loading"
                }`}>
              <Home className="me-3" />
              Check-In
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const CheckOutForm = ({
  state,
  setState,
  onSubmit,
  isSubmitting,
  isFetching,
}) => {
  return (
    <div>
      <div autoComplete="off" className="row">
        <div className="col-12 mb-3">
          <div className="field">
            <label className="mb-0 label" htmlFor="lastName">
              Last Name
              <RequiredIndicator />
            </label>
            <input
              required
              id="lastName"
              placeholder="last name"
              autoComplete="new-p"
              value={state?.lastName}
              onChange={(e) =>
                setState({ field: "lastName", value: e.target.value })
              }
              type="text"
              className="input is-large"
            />
          </div>
        </div>
        <div className="col-12 mb-3">
          <div className="field">
            <label className="mb-0 label" htmlFor="contact">
              Phone Number
              <RequiredIndicator />
            </label>
            <input
              required
              id="contact"
              placeholder="active phone number"
              autoComplete="new"
              type="tel"
              value={state?.contact}
              onChange={(e) =>
                setState({ field: "contact", value: e.target.value })
              }
              className="input is-large"
            />
          </div>
        </div>
        {/* <>
                <div className="col-12 mb-3">
                    <div className="field">
                        <label className="mb-0 label" htmlFor="notes">
                            Notes
                        </label>
                        <textarea
                            id="notes"
                            // autoComplete="new"
                            value={ state?.notes }
                            onChange={ e => setState( { ...state, notes: e.target.value } ) }
                            placeholder="share additional information (optional)"
                            className="textarea is-large" />
                    </div>
                </div>
            </> */}
        <div className="col-12 text-center">
          <button
            onClick={onSubmit}
            className={`button h6 is-large app-btn btn-prim mt-3 ${isSubmitting && "is-loading"
              }`}>
            <LogOut className="me-3" />
            Correct, Check-Out
          </button>
        </div>
      </div>
    </div>
  );
};
